/**
 * AdminResultsBracket.js
 * Visual bracket for entering game results — click a team to mark winner.
 * Auto-saves and propagates to next round instantly.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';

const GAME_FEEDS_INTO = {
  'r64-s1':['r32-s1',0],'r64-s2':['r32-s1',1],'r64-s3':['r32-s2',0],'r64-s4':['r32-s2',1],
  'r64-s5':['r32-s3',0],'r64-s6':['r32-s3',1],'r64-s7':['r32-s4',0],'r64-s8':['r32-s4',1],
  'r64-e1':['r32-e1',0],'r64-e2':['r32-e1',1],'r64-e3':['r32-e2',0],'r64-e4':['r32-e2',1],
  'r64-e5':['r32-e3',0],'r64-e6':['r32-e3',1],'r64-e7':['r32-e4',0],'r64-e8':['r32-e4',1],
  'r64-m1':['r32-m1',0],'r64-m2':['r32-m1',1],'r64-m3':['r32-m2',0],'r64-m4':['r32-m2',1],
  'r64-m5':['r32-m3',0],'r64-m6':['r32-m3',1],'r64-m7':['r32-m4',0],'r64-m8':['r32-m4',1],
  'r64-w1':['r32-w1',0],'r64-w2':['r32-w1',1],'r64-w3':['r32-w2',0],'r64-w4':['r32-w2',1],
  'r64-w5':['r32-w3',0],'r64-w6':['r32-w3',1],'r64-w7':['r32-w4',0],'r64-w8':['r32-w4',1],
  'r32-s1':['r16-s1',0],'r32-s2':['r16-s1',1],'r32-s3':['r16-s2',0],'r32-s4':['r16-s2',1],
  'r32-e1':['r16-e1',0],'r32-e2':['r16-e1',1],'r32-e3':['r16-e2',0],'r32-e4':['r16-e2',1],
  'r32-m1':['r16-m1',0],'r32-m2':['r16-m1',1],'r32-m3':['r16-m2',0],'r32-m4':['r16-m2',1],
  'r32-w1':['r16-w1',0],'r32-w2':['r16-w1',1],'r32-w3':['r16-w2',0],'r32-w4':['r16-w2',1],
  'r16-s1':['r8-s',0],'r16-s2':['r8-s',1],'r16-e1':['r8-e',0],'r16-e2':['r8-e',1],
  'r16-m1':['r8-m',0],'r16-m2':['r8-m',1],'r16-w1':['r8-w',0],'r16-w2':['r8-w',1],
  'r4-1':['final-1',0],'r4-2':['final-1',1],
};

const R8_TO_R4 = {
  'r8-w': ['r4-1', 0],
  'r8-e': ['r4-1', 1],
  'r8-s': ['r4-2', 0],
  'r8-m': ['r4-2', 1],
};

function getR8Feed(rl, regionOrder) {
  if (regionOrder && regionOrder.length === 4) {
    const map = {};
    map[regionOrder[0][0].toLowerCase()] = ['r4-1', 0];
    map[regionOrder[3][0].toLowerCase()] = ['r4-1', 1];
    map[regionOrder[1][0].toLowerCase()] = ['r4-2', 0];
    map[regionOrder[2][0].toLowerCase()] = ['r4-2', 1];
    return map[rl] || null;
  }
  // Fallback to hardcoded current setup
  return R8_TO_R4[`r8-${rl}`] || null;
}

// GameCard — click a team to pick winner
function GameCard({ game, onPick, saving, gamesById = {} }) {
  if (!game) return <div style={{height:52}} />;
  const isFinal = game.status === 'final';
  const isLive  = game.status === 'live';
  const isSaving = saving === game.id;

  // Resolve a slot — if it has a playInGameId, substitute the actual winner
  function resolveSlot(slot) {
    if (!slot) return slot;
    if (slot.playInGameId) {
      const pg = gamesById[slot.playInGameId];
      if (pg?.winner) {
        const winner = pg.homeTeam?.id === pg.winner ? pg.homeTeam : pg.awayTeam;
        if (winner) return winner;
      }
      // Play-in not yet complete — show team names as placeholder
      if (pg?.homeTeam && pg?.awayTeam) {
        const abbr = t => t.name?.split(' ').pop() || '?';
        return { ...slot, name: `${abbr(pg.homeTeam)} / ${abbr(pg.awayTeam)}` };
      }
    }
    return slot;
  }

  const homeTeam = resolveSlot(game.homeTeam);
  const awayTeam = resolveSlot(game.awayTeam);

  const Team = ({ team }) => {
    const isTbd = !team || team.id === 'tbd' || team.id?.includes('winner') || team.id?.includes('playin');
    const isWin = isFinal && team && game.winner === team.id;
    const canClick = !isTbd && !isSaving;
    const label = isTbd
      ? (team?.name?.startsWith('Play-In') ? team.name : 'TBD')
      : `(${team?.seed}) ${team?.name}`;
    return (
      <div
        onClick={() => canClick && onPick(game, isWin ? null : team)}
        style={{
          padding: '5px 8px', display:'flex', alignItems:'center', gap:4,
          cursor: canClick ? 'pointer' : 'default',
          background: isWin ? 'rgba(39,174,96,0.18)' : 'transparent',
          borderLeft: isWin ? '3px solid #27ae60' : '3px solid transparent',
          minHeight: 22,
        }}
        onMouseEnter={e => { if(canClick&&!isWin) e.currentTarget.style.background='rgba(75,139,190,0.12)'; }}
        onMouseLeave={e => { if(!isWin) e.currentTarget.style.background='transparent'; }}
      >
        <span style={{
          fontFamily:'var(--font-condensed)', fontSize:'0.78rem', flex:1,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          color: isTbd ? '#3a5a80' : isWin ? '#27ae60' : 'var(--cream)',
          fontWeight: isWin ? 700 : 400,
        }}>{label}</span>
        {isWin && <span style={{color:'#27ae60',fontSize:'0.65rem',flexShrink:0}}>✓</span>}
      </div>
    );
  };

  return (
    <div style={{
      background:'var(--navy-light)',
      border:`1px solid ${isFinal?'rgba(39,174,96,0.45)':isLive?'#27ae60':'#2a4470'}`,
      borderRadius:5, overflow:'hidden', position:'relative',
      opacity:isSaving?0.6:1, width:'100%',
    }}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
        padding:'1px 5px',background:'rgba(0,0,0,0.2)',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
        <span style={{fontSize:'0.58rem',fontFamily:'var(--font-condensed)',
          color:isFinal?'#27ae60':isLive?'#2ecc71':'#3a5a80',letterSpacing:'0.5px'}}>
          {isFinal?'FINAL':isLive?'● LIVE':game.id}
        </span>
        {isFinal && <button onClick={e=>{e.stopPropagation();onPick(game,null);}}
          style={{background:'none',border:'none',color:'#3a5a80',cursor:'pointer',
            fontSize:'0.6rem',padding:'0 2px',lineHeight:1}} title="Clear">✕</button>}
        {isSaving && <span style={{fontSize:'0.58rem',color:'var(--blue)'}}>…</span>}
      </div>
      <Team team={homeTeam} />
      <div style={{height:1,background:'rgba(255,255,255,0.05)'}} />
      <Team team={awayTeam} />
    </div>
  );
}

// BracketRound — one column of games, evenly spaced vertically
// groupSize = how many R64 games each game here "covers" (R64=1, R32=2, R16=4, R8=8)
function BracketRound({ ids, gamesById, onPick, saving, label, groupSize, totalSlots }) {
  // Each game occupies (groupSize / totalSlots) of total height
  // We render them using flex with equal spacer gaps
  const slotFraction = groupSize / totalSlots; // e.g. R32 game covers 2/8 = 25%
  const cardW = 175;

  return (
    <div style={{width:cardW+16,flexShrink:0,display:'flex',flexDirection:'column'}}>
      <div style={{textAlign:'center',fontFamily:'var(--font-condensed)',fontSize:'0.6rem',
        color:'#4a6a90',letterSpacing:'1px',marginBottom:4,textTransform:'uppercase'}}>{label}</div>
      <div style={{flex:1,display:'flex',flexDirection:'column'}}>
        {ids.map((gid, i) => (
          <div key={gid} style={{
            flex: groupSize,
            display:'flex', alignItems:'center', justifyContent:'center',
            padding:'2px 8px',
          }}>
            <GameCard game={gamesById[gid]} onPick={onPick} saving={saving} gamesById={gamesById} />
          </div>
        ))}
      </div>
    </div>
  );
}

// Full region: R64 | R32 | S16 | E8
function RegionBracket({ region, gamesById, onPick, saving }) {
  const rl = region[0].toLowerCase();
  const totalSlots = 8;
  const rounds = [
    { label:'R64',      ids:[1,2,3,4,5,6,7,8].map(n=>`r64-${rl}${n}`), size:1 },
    { label:'R32',      ids:[1,2,3,4].map(n=>`r32-${rl}${n}`), size:2 },
    { label:'SWEET 16', ids:[1,2].map(n=>`r16-${rl}${n}`), size:4 },
    { label:'ELITE 8',  ids:[`r8-${rl}`], size:8 },
  ];
  const ROW_H = 58; // height per R64 slot
  const totalH = totalSlots * ROW_H;

  return (
    <div>
      <div style={{fontFamily:'var(--font-display)',fontSize:'0.9rem',color:'var(--blue)',
        letterSpacing:'2px',textAlign:'center',marginBottom:10}}>
        {region.toUpperCase()} REGION
      </div>
      <div style={{display:'flex',flexDirection:'row',height:totalH,alignItems:'stretch'}}>
        {rounds.map(round => (
          <BracketRound
            key={round.label}
            ids={round.ids}
            gamesById={gamesById}
            onPick={onPick}
            saving={saving}
            label={round.label}
            groupSize={round.size}
            totalSlots={totalSlots}
          />
        ))}
      </div>
    </div>
  );
}

function FinalFourPanel({ gamesById, onPick, saving, wiring }) {
  const ff = wiring?.finalFour || { semi1:['West','East'], semi2:['South','Midwest'] };
  return (
    <div style={{display:'flex',flexDirection:'column',gap:16,maxWidth:280}}>
      <div style={{fontFamily:'var(--font-display)',fontSize:'0.9rem',color:'var(--blue)',
        letterSpacing:'2px'}}>FINAL FOUR</div>
      <div>
        <div style={{fontSize:'0.68rem',fontFamily:'var(--font-condensed)',color:'var(--gray)',marginBottom:4}}>
          {ff.semi1.join(' vs ')}
        </div>
        <GameCard game={gamesById['r4-1']} onPick={onPick} saving={saving} gamesById={gamesById} />
      </div>
      <div>
        <div style={{fontSize:'0.68rem',fontFamily:'var(--font-condensed)',color:'var(--gray)',marginBottom:4}}>
          {ff.semi2.join(' vs ')}
        </div>
        <GameCard game={gamesById['r4-2']} onPick={onPick} saving={saving} gamesById={gamesById} />
      </div>
      <div style={{fontFamily:'var(--font-display)',fontSize:'0.9rem',color:'#b8860b',letterSpacing:'2px',marginTop:8}}>
        CHAMPIONSHIP
      </div>
      <GameCard game={gamesById['final-1']} onPick={onPick} saving={saving} gamesById={gamesById} />
    </div>
  );
}

function PlayInPanel({ gamesById, onPick, saving, playInRoundName = 'First Four' }) {
  const ids = ['playin-1','playin-2','playin-3','playin-4'].filter(id=>gamesById[id]);
  if (!ids.length) return <div style={{color:'var(--gray)',fontFamily:'var(--font-condensed)'}}>No play-in games configured.</div>;
  return (
    <div>
      <div style={{fontFamily:'var(--font-condensed)',fontSize:'0.75rem',color:'var(--gray)',
        letterSpacing:'2px',marginBottom:10}}>{playInRoundName.toUpperCase()} PLAY-IN GAMES</div>
      <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
        {ids.map(id=>(
          <div key={id} style={{width:200}}>
            <GameCard game={gamesById[id]} onPick={onPick} saving={saving} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminResultsBracket({ wiring, playInRoundName = 'First Four' }) {
  const api = useApi();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [activeTab, setActiveTab] = useState('playin');
  const [undoStack, setUndoStack] = useState([]);
  const regionOrder = wiring?.regionOrder || ['South','East','Midwest','West'];
  const wiringReady = !!wiring?.finalFour;

  const load = useCallback(async () => {
    try { const d = await api.adminGetGames(); setGames(Array.isArray(d)?d:[]); }
    catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(()=>{ load(); },[load]);

  const gamesById = {};
  games.forEach(g=>{ gamesById[g.id]=g; });

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),2000); };

  const TBD_SLOT = {id:'tbd',name:'TBD',abbrev:'TBD',seed:0,score:0};

  const propagate = (gameId, winnerTeam, byId) => {
    const rl = gameId.startsWith('r8-') ? gameId.replace('r8-','') : null;
    const entry = GAME_FEEDS_INTO[gameId] || (rl ? getR8Feed(rl, regionOrder) : null);
    if (rl) console.log(`[PROPAGATE] r8-${rl} → entry:`, entry, '| wiring.finalFour:', wiring?.finalFour);

    // Play-in → R64: look up feedsIntoGameId from bracketWiring config
    if (!entry && gameId.startsWith('playin-')) {
      const playInConfig = wiring?.playIn?.find(p => p.id === gameId);
      if (playInConfig?.feedsIntoGameId) {
        const nextId = playInConfig.feedsIntoGameId;
        const slot = playInConfig.feedsIntoSlot === 'home' ? 0 : 1;
        const next = byId[nextId]; if (!next) return;
        const upd = { ...next };
        if (winnerTeam) {
          if (slot===0) upd.homeTeam = {...winnerTeam,score:0};
          else upd.awayTeam = {...winnerTeam,score:0};
        } else {
          if (slot===0) upd.homeTeam = TBD_SLOT;
          else upd.awayTeam = TBD_SLOT;
        }
        // Always clear winner and cascade downstream — team changed so all results invalid
        if (next.winner || winnerTeam) {
          upd.winner = null;
          upd.status = 'scheduled';
          byId[nextId] = upd;
          propagate(nextId, null, byId);
        } else {
          byId[nextId] = upd;
        }
      }
      return;
    }

    if (!entry) return;
    const [nextId, slot] = entry;
    const next = byId[nextId]; if (!next) return;
    const upd = { ...next };
    if (winnerTeam) {
      // Set the winner as the team in the next round slot
      if (slot===0) upd.homeTeam = {...winnerTeam,score:0};
      else upd.awayTeam = {...winnerTeam,score:0};
    } else {
      // Clearing winner — revert next round slot back to TBD
      if (slot===0) upd.homeTeam = TBD_SLOT;
      else upd.awayTeam = TBD_SLOT;
    }
    // If the next game already had a winner, clear it too and cascade
    if (next.winner) {
      upd.winner = null;
      upd.status = 'scheduled';
      byId[nextId] = upd;
      propagate(nextId, null, byId); // cascade clear further rounds
    } else {
      byId[nextId] = upd;
    }
  };

  const handlePick = async (game, winner) => {
    if (saving) return;
    if (!wiringReady) { setError('Wiring not loaded yet — please wait a moment and try again.'); return; }
    setUndoStack(p=>[...p.slice(-9),{gameId:game.id,prevWinner:game.winner,prevStatus:game.status}]);
    const byId = {}; games.forEach(g=>{ byId[g.id]={...g}; });
    byId[game.id] = {...game, winner:winner?.id||null, status:winner?'final':(game.status==='live'?'live':'scheduled')};
    propagate(game.id, winner || null, byId); // always propagate — clears TBD when winner=null
    setGames(Object.values(byId));
    setSaving(game.id);
    try {
      await api.adminUpdateGame(game.id, {
        winner:winner?.id||null, status:winner?'final':(game.status==='live'?'live':'scheduled'),
        homeScore:game.homeTeam?.score||0, awayScore:game.awayTeam?.score||0,
      });
      // When clearing a winner, also save all affected downstream games back to Firestore
      if (!winner) {
        const affectedIds = [];
        const collectAffected = (gid) => {
          const rl2 = gid.startsWith('r8-') ? gid.replace('r8-','') : null;
          const e = GAME_FEEDS_INTO[gid] || (rl2 ? getR8Feed(rl2, regionOrder) : null);
          // Also handle play-in → R64
          if (!e && gid.startsWith('playin-')) {
            const pc = wiring?.playIn?.find(p => p.id === gid);
            if (pc?.feedsIntoGameId && byId[pc.feedsIntoGameId]) {
              affectedIds.push(pc.feedsIntoGameId);
              collectAffected(pc.feedsIntoGameId);
            }
            return;
          }
          if (!e) return;
          const [nextId] = e;
          if (byId[nextId]) { affectedIds.push(nextId); collectAffected(nextId); }
        };
        collectAffected(game.id);
        for (const nextId of affectedIds) {
          const ng = byId[nextId];
          if (ng) await api.adminUpdateGame(nextId, {
            winner: null, status: 'scheduled', homeScore: 0, awayScore: 0,
            homeTeam: ng.homeTeam, awayTeam: ng.awayTeam,
          });
        }
      } else if (game.id.startsWith('playin-')) {
        // When changing a play-in winner, save the R64 slot AND all cascade-cleared downstream games
        const pc = wiring?.playIn?.find(p => p.id === game.id);
        if (pc?.feedsIntoGameId) {
          const r64Id = pc.feedsIntoGameId;
          const r64Game = byId[r64Id];
          if (r64Game) {
            await api.adminUpdateGame(r64Id, {
              winner: r64Game.winner || null,
              status: r64Game.status || 'scheduled',
              homeTeam: r64Game.homeTeam, awayTeam: r64Game.awayTeam,
              homeScore: 0, awayScore: 0,
            });
            // Save all further downstream games that got cascade-cleared
            const affectedIds = [];
            const collectAffected = (gid) => {
              const rl2 = gid.startsWith('r8-') ? gid.replace('r8-','') : null;
              const e = GAME_FEEDS_INTO[gid] || (rl2 ? getR8Feed(rl2, regionOrder) : null);
              if (!e) return;
              const [nId] = e;
              if (byId[nId]) { affectedIds.push(nId); collectAffected(nId); }
            };
            collectAffected(r64Id);
            for (const nextId of affectedIds) {
              const ng = byId[nextId];
              if (ng) await api.adminUpdateGame(nextId, {
                winner: null, status: 'scheduled', homeScore: 0, awayScore: 0,
                homeTeam: ng.homeTeam, awayTeam: ng.awayTeam,
              });
            }
          }
        }
      }
      showToast(winner ? `✓ ${winner.name}` : '✓ Cleared');
    } catch(e) { setError(e.message); load(); }
    finally { setSaving(null); }
  };

  const handleUndo = async () => {
    if (!undoStack.length||saving) return;
    const prev = undoStack[undoStack.length-1];
    setUndoStack(s=>s.slice(0,-1));
    const game = gamesById[prev.gameId]; if (!game) return;
    const byId = {}; games.forEach(g=>{ byId[g.id]={...g}; });
    byId[prev.gameId] = {...game,winner:prev.prevWinner||null,status:prev.prevStatus||'scheduled'};
    setGames(Object.values(byId));
    setSaving(prev.gameId);
    try {
      await api.adminUpdateGame(prev.gameId,{winner:prev.prevWinner||null,status:prev.prevStatus||'scheduled',
        homeScore:game.homeTeam?.score||0,awayScore:game.awayTeam?.score||0});
      showToast('↩ Undone');
    } catch(e) { setError(e.message); load(); }
    finally { setSaving(null); }
  };

  const finalCount = games.filter(g=>g.status==='final').length;
  const tabs = [
    {key:'playin',label:playInRoundName.toUpperCase()},
    ...regionOrder.map(r=>({key:r,label:r.toUpperCase()})),
    {key:'finals',label:'FINAL FOUR'},
  ];

  if (loading) return <div style={{padding:40,textAlign:'center',color:'var(--gray)'}}>Loading...</div>;

  return (
    <div>
      {/* Stats + controls */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          <div>
            <div style={{fontFamily:'var(--font-condensed)',fontSize:'0.8rem',color:'var(--gray)'}}>
              {finalCount} / {games.length} games final
            </div>
            <div style={{width:160,height:4,background:'#1a3050',borderRadius:2,marginTop:3}}>
              <div style={{width:`${games.length?(finalCount/games.length*100):0}%`,height:'100%',
                background:'#27ae60',borderRadius:2,transition:'width 0.4s'}} />
            </div>
          </div>
          {toast && <span style={{fontFamily:'var(--font-condensed)',fontSize:'0.85rem',color:'var(--green)'}}>{toast}</span>}
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-secondary btn-sm" onClick={handleUndo} disabled={!undoStack.length||!!saving}>↩ Undo</button>
          <button className="btn btn-secondary btn-sm" onClick={load}>↺ Refresh</button>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{marginBottom:10}}>
        {error} <button onClick={()=>setError('')} style={{float:'right',background:'none',border:'none',color:'inherit',cursor:'pointer'}}>✕</button>
      </div>}

      <div style={{fontFamily:'var(--font-condensed)',fontSize:'0.72rem',color:'var(--gray)',
        marginBottom:12,padding:'5px 10px',background:'rgba(75,139,190,0.06)',borderRadius:4,
        border:'1px solid rgba(75,139,190,0.12)'}}>
        💡 Click a team to set them as winner — auto-saves and advances to next round. Click ✕ to clear.
      </div>

      {/* Tabs */}
      <div className="tabs" style={{marginBottom:18,flexWrap:'wrap'}}>
        {tabs.map(t => {
          const regionDone = regionOrder.includes(t.key)
            ? games.filter(g=>g.region===t.key&&g.status==='final').length : 0;
          return (
            <button key={t.key} className={`tab${activeTab===t.key?' active':''}`}
              onClick={()=>setActiveTab(t.key)} style={{fontSize:'0.78rem'}}>
              {t.label}
              {regionDone>0 && <span style={{marginLeft:4,fontSize:'0.62rem',color:'var(--green)',fontWeight:700}}>{regionDone}</span>}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{overflowX:'auto',paddingBottom:16}}>
        {activeTab==='playin' && <PlayInPanel gamesById={gamesById} onPick={handlePick} saving={saving} playInRoundName={playInRoundName} />}
        {regionOrder.includes(activeTab) && <RegionBracket region={activeTab} gamesById={gamesById} onPick={handlePick} saving={saving} />}
        {activeTab==='finals' && <FinalFourPanel gamesById={gamesById} onPick={handlePick} saving={saving} wiring={wiring} />}
      </div>
    </div>
  );
}
